import React from 'react';
import { useNavigate } from 'react-router-dom';

interface MatchHistoryItem {
  id: string;
  matchId: string;
  matchDate: string;
  location: string;
  teamName: string;
  position: string;
  goals: number;
  assists: number;
  saves: number;
  isMVP: boolean;
  isTopScorer: boolean;
  isTopAssister: boolean;
  isTopSaver: boolean;
  matchResult?: 'WIN' | 'LOSS' | 'DRAW';
  goalDifference?: number | null;
  hasPendingValidations?: boolean;
  pendingValidationsCount?: number;
}

// Un partido de "registro personal" (cargado a mano / importado) no tiene goles
// individuales ni equipo ni un match real navegable.
const isPersonalLogMatch = (m: { location?: string; matchId?: string }) =>
  m.location === 'Registro personal' || /^(manual-|mifutbol-)/.test(m.matchId || '');

const diffLabel = (d?: number | null): string => {
  if (d === null || d === undefined) return '';
  if (d > 0) return `+${d}`;
  if (d < 0) return `${d}`;
  return '=';
};

interface MatchHistoryProps {
  matches: MatchHistoryItem[];
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onMatchClick?: (matchId: string) => void;
  isMobile?: boolean;
}

export const MatchHistory: React.FC<MatchHistoryProps> = ({
  matches,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  onMatchClick,
  isMobile = false
}) => {
  const navigate = useNavigate();

  const handleMatchClick = (matchId: string) => {
    if (onMatchClick) {
      onMatchClick(matchId);
    } else {
      navigate(`/match/${matchId}`);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getPositionEmoji = (position: string) => {
    switch (position) {
      case 'Arquero': return '🧤';
      case 'Defensor': return '🛡️';
      case 'Mediocampista': return '⚡';
      case 'Delantero': return '⚽';
      default: return '👤';
    }
  };

  const getMatchResultColor = (matchResult?: 'WIN' | 'LOSS' | 'DRAW') => {
    switch (matchResult) {
      case 'WIN': return '#2C2F33';
      case 'LOSS': return '#2C2F33';
      case 'DRAW': return '#2C2F33';
      default: return '#2C2F33';
    }
  };

  return (
    <div style={{
      backgroundColor: '#23272A',
      padding: isMobile ? '14px' : '24px',
      borderRadius: '18px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      border: '1px solid #33383B'
    }}>
      <h2 style={{ 
        fontSize: isMobile ? '17px' : '22px', 
        fontWeight: 'bold', 
        margin: '0 0 14px 0',
        color: '#00E676'
      }}>
        📅 Partidos Jugados
      </h2>
      
      {!matches || matches.length === 0 ? (
        <div style={{ textAlign: 'center', padding: isMobile ? '24px' : '40px', color: '#B0BEC5' }}>
          <div style={{ fontSize: isMobile ? '32px' : '48px', marginBottom: '16px' }}>⚽</div>
          <div style={{ fontSize: isMobile ? '15px' : '18px', marginBottom: '8px' }}>No hay partidos registrados</div>
          <div style={{ fontSize: isMobile ? '12px' : '14px' }}>Los partidos aparecerán aquí después de ser finalizados</div>
        </div>
      ) : (
        <>
          {matches?.map((match) => {
            const isPersonal = isPersonalLogMatch(match);
            const resultColor = match.matchResult === 'WIN' ? '#00E676' : match.matchResult === 'LOSS' ? '#FF6B6B' : '#B0BEC5';
            return (
            <button
              key={match.id}
              type="button"
              onClick={() => { if (!isPersonal) handleMatchClick(match.matchId); }}
              style={{
                backgroundColor: getMatchResultColor(match.matchResult),
                padding: isMobile ? '10px' : '16px',
                borderRadius: '12px',
                marginBottom: isMobile ? '8px' : '12px',
                border: '1px solid #40444B',
                cursor: isPersonal ? 'default' : 'pointer',
                transition: 'all 0.2s ease',
                fontSize: isMobile ? '13px' : '15px',
                minWidth: 0,
                width: '100%',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                if (isPersonal) return;
                e.currentTarget.style.backgroundColor = '#40444B';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
              }}
              onMouseLeave={(e) => {
                if (isPersonal) return;
                e.currentTarget.style.backgroundColor = getMatchResultColor(match.matchResult);
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: isMobile ? '8px' : '10px' 
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginBottom: '4px'
                  }}>
                    <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 'bold', color: '#FFFFFF' }}>
                      {formatDate(match.matchDate)}
                    </div>
                    {match.matchResult && (
                      <span style={{
                        padding: isMobile ? '2px 6px' : '3px 8px',
                        borderRadius: '12px',
                        fontSize: isMobile ? '10px' : '11px',
                        fontWeight: 'bold',
                        backgroundColor: match.matchResult === 'WIN' ? '#00E676' : 
                                        match.matchResult === 'LOSS' ? '#FF6B6B' : '#B0BEC5',
                        color: match.matchResult === 'WIN' ? '#181A1B' : 
                               match.matchResult === 'LOSS' ? '#FFFFFF' : '#181A1B'
                      }}>
                        {match.matchResult === 'WIN' ? 'Victoria' : 
                         match.matchResult === 'LOSS' ? 'Derrota' : 'Empate'}
                      </span>
                    )}
                  </div>
                  {!isPersonal && (
                    <>
                      <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#B0BEC5' }}>
                        {match.teamName}
                      </div>
                      <div style={{
                        fontSize: isMobile ? '11px' : '12px',
                        color: '#8FA3AD',
                        marginTop: '2px'
                      }}>
                        {getPositionEmoji(match.position)} {match.position}
                      </div>
                    </>
                  )}
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {isPersonal ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '10px' }}>
                      {(match.goals > 0 || match.assists > 0 || match.saves > 0) && (
                        <div style={{
                          display: 'flex',
                          gap: isMobile ? '4px' : '6px',
                          fontSize: isMobile ? '12px' : '14px',
                          color: '#B0BEC5'
                        }}>
                          {match.goals > 0 && <span title="Goles">⚽ {match.goals}</span>}
                          {match.assists > 0 && <span title="Asistencias">➡️ {match.assists}</span>}
                          {match.saves > 0 && <span title="Atajadas">🧤 {match.saves}</span>}
                        </div>
                      )}
                      {(match.goalDifference !== null && match.goalDifference !== undefined) && (
                        <span title="Diferencia de goles" style={{
                          fontSize: isMobile ? '18px' : '22px',
                          fontWeight: 800,
                          color: resultColor
                        }}>
                          {diffLabel(match.goalDifference)}
                        </span>
                      )}
                    </div>
                  ) : (
                  <div style={{
                    display: 'flex',
                    gap: isMobile ? '4px' : '6px',
                    fontSize: isMobile ? '12px' : '14px',
                    color: '#B0BEC5'
                  }}>
                    <span title="Goles">⚽ {match.goals}</span>
                    <span title="Asistencias">➡️ {match.assists}</span>
                    <span title="Atajadas">🧤 {match.saves}</span>
                  </div>
                  )}

                  {!isPersonal && match.isTopScorer && (
                    <span style={{
                      backgroundColor: '#00E676',
                      color: '#181A1B',
                      padding: '2px 6px',
                      borderRadius: '8px',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}>Goleador</span>
                  )}
                  {!isPersonal && match.isTopSaver && (
                    <span style={{
                      backgroundColor: '#00B8D9',
                      color: '#181A1B',
                      padding: '2px 6px',
                      borderRadius: '8px',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}>Guante de Oro</span>
                  )}
                </div>
              </div>
            </button>
            );
          })}

          {/* Paginación */}
          {totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '8px', 
              marginTop: '20px' 
            }}>
              <button
                onClick={() => onPageChange && onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: isMobile ? '6px 10px' : '8px 16px',
                  backgroundColor: currentPage === 1 ? '#40444B' : '#00E676',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '12px' : '14px'
                }}
              >
                Anterior
              </button>
              <span style={{ 
                padding: isMobile ? '6px 10px' : '8px 16px', 
                color: '#B0BEC5',
                fontSize: isMobile ? '12px' : '14px'
              }}>
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => onPageChange && onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: isMobile ? '6px 10px' : '8px 16px',
                  backgroundColor: currentPage === totalPages ? '#40444B' : '#00E676',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '12px' : '14px'
                }}
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}; 